import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { skipToken, useQuery } from '@tanstack/react-query';

import { authenticationAPIService } from '~/api/login';
import Toast from '~/components/atoms/Toast';
import { userKeys } from '~/constants/queryKeys';
import { USER_EMAIL, USER_ID } from '~/constants/storages';
import { useAppDispatch } from '~/hooks/useRedux';
import { cookieService } from '~/services/cookie-service';
import { visitorAttributionService } from '~/services/visitorAttribution';
import { setWindowSizes } from '~/store/reducers/app/slice';

import Router from './router';

function App() {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const isAccessCookie = cookieService.getCookie('access') || null;

  const { data: user } = useQuery({
    queryKey: [userKeys.user, isAccessCookie],
    queryFn: isAccessCookie ? () => authenticationAPIService.getAccount() : skipToken,
    select: (response) => {
      return response.data;
    },
  });

  // Tracks user information if available and sends data to the analytics layer.
  useEffect(() => {
    if (user?.id && window.dataLayer) {
      localStorage.setItem(USER_ID, String(user.id));
      window.dataLayer.push({
        user_id: String(user.id),
      });
    }

    if (user?.email && window.dataLayer) {
      localStorage.setItem(USER_EMAIL, user.email);
      window.dataLayer.push({
        user_data: {
          email_address: user.email,
        },
      });
    }
  }, [user?.id, user?.email]);

  useEffect(() => {
    // Track visitor attribution on initial load
    void visitorAttributionService.trackLanding();

    // TODO should be removed in future when we fully move to media queries from mui
    function handleResize() {
      dispatch(
        setWindowSizes({
          width: window.innerWidth,
          height: window.innerHeight,
        }),
      );
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <>
      {process.env.ENV === 'FE_STAGE' && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
        </Helmet>
      )}
      <Router />
      <Toast />
    </>
  );
}

export default App;
